(() => {
  const MODAL_SELECTOR = '[data-patchouli-delivery-modal]';
  const REQUIRED_SELECTOR = '[data-patchouli-required]';
  const DRINK_SELECTOR = '[data-patchouli-wine-variant]';
  const OPEN_CLASS = 'is-open';
  const BODY_CLASS = 'patchouli-modal-open';
  const APPROVED_FLAG = 'patchouliDeliveryApproved';

  let activeModal = null;
  let previouslyFocused = null;

  function getFormModal(form) {
    if (!(form instanceof HTMLFormElement)) return null;

    const modalInsideForm = form.querySelector(MODAL_SELECTOR);

    if (modalInsideForm) {
      modalInsideForm.dataset.patchouliFormId = form.id;
      return modalInsideForm;
    }

    if (!form.id) return null;

    return document.querySelector(
      `${MODAL_SELECTOR}[data-patchouli-form-id="${CSS.escape(form.id)}"]`
    );
  }

  function getFocusable(modal) {
    if (!modal) return [];

    return Array.from(
      modal.querySelectorAll(
        [
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[href]',
          '[tabindex]:not([tabindex="-1"])',
        ].join(',')
      )
    ).filter((element) => {
      return (
        element.offsetParent !== null &&
        !element.hasAttribute('hidden')
      );
    });
  }

  function localDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function prepareModal(modal) {
    if (!modal) return;

    const date = modal.querySelector('[data-patchouli-delivery-date]');
    if (date) date.min = localDateString();

    const message = modal.querySelector('[data-patchouli-message]');
    const counter = modal.querySelector('[data-patchouli-message-count]');

    if (message && counter) {
      counter.textContent = String(message.value.length);
    }

    updateDrinkSelection(modal);
  }

  function moveModalToBody(modal) {
    if (!modal || modal.parentElement === document.body) return;

    const parent = modal.parentElement;
    if (!parent) return;

    modal.__patchouliOriginalParent = parent;
    modal.__patchouliOriginalNextSibling = modal.nextSibling;

    document.body.appendChild(modal);
  }

  function restoreModalPosition(modal) {
    if (!modal) return;

    const parent = modal.__patchouliOriginalParent;
    const nextSibling = modal.__patchouliOriginalNextSibling;

    if (!(parent instanceof HTMLElement)) return;

    if (nextSibling && nextSibling.parentNode === parent) {
      parent.insertBefore(modal, nextSibling);
    } else {
      parent.appendChild(modal);
    }

    modal.__patchouliOriginalParent = null;
    modal.__patchouliOriginalNextSibling = null;
  }

  function showStep(modal, step) {
    if (!modal) return;

    const stepNumber = String(step);

    modal.querySelectorAll('[data-patchouli-step]').forEach((panel) => {
      panel.hidden = panel.dataset.patchouliStep !== stepNumber;
    });

    modal.querySelectorAll('[data-patchouli-progress]').forEach((item) => {
      const number = Number(item.dataset.patchouliProgress);
      const current = Number(step);

      item.classList.toggle('is-active', number === current);
      item.classList.toggle('is-complete', number < current);
    });

    modal.dataset.patchouliCurrentStep = stepNumber;

    const dialog = modal.querySelector('.patchouli-delivery-modal__dialog');

    if (dialog) {
      dialog.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    window.requestAnimationFrame(() => {
      const panel = modal.querySelector(
        `[data-patchouli-step="${stepNumber}"]:not([hidden])`
      );

      const focusTarget = panel?.querySelector(
        'input:not([type="checkbox"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
      );

      focusTarget?.focus();
    });
  }

  function openModal(modal) {
    if (!modal || modal.classList.contains(OPEN_CLASS)) return;

    activeModal = modal;
    previouslyFocused = document.activeElement;

    prepareModal(modal);
    showStep(modal, 1);
    moveModalToBody(modal);

    modal.classList.add(OPEN_CLASS);
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_CLASS);

    window.requestAnimationFrame(() => {
      const firstField = modal.querySelector(
        '[data-patchouli-step="1"] input:not([disabled]), [data-patchouli-step="1"] select:not([disabled]), [data-patchouli-step="1"] textarea:not([disabled])'
      );

      const dialog = modal.querySelector('.patchouli-delivery-modal__dialog');
      (firstField || dialog)?.focus();
    });
  }

  function closeModal(modal, restoreFocus = true) {
    if (!modal) return;

    modal.classList.remove(OPEN_CLASS);
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_CLASS);

    hideValidationError(modal);
    hideCartError(modal);
    restoreModalPosition(modal);

    if (restoreFocus && previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }

    activeModal = null;
  }

  function hideValidationError(modal) {
    const error = modal?.querySelector('[data-patchouli-modal-error]');
    if (error) error.hidden = true;
  }

  function showCartError(modal, message) {
    const error = modal?.querySelector('[data-patchouli-cart-error]');
    if (!error) return;

    if (message) error.textContent = message;
    error.hidden = false;
  }

  function hideCartError(modal) {
    const error = modal?.querySelector('[data-patchouli-cart-error]');
    if (error) error.hidden = true;
  }

  function markField(field, invalid) {
    if (!field) return;

    field.classList.toggle('is-invalid', invalid);

    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-invalid');
    }
  }

  function validateStepOne(modal) {
    if (!modal) return false;

    const stepOne = modal.querySelector('[data-patchouli-step="1"]');
    if (!stepOne) return false;

    let firstInvalid = null;
    let valid = true;

    stepOne.querySelectorAll(REQUIRED_SELECTOR).forEach((field) => {
      const empty = !String(field.value || '').trim();

      markField(field, empty);

      if (empty) {
        valid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    });

    const phone = stepOne.querySelector(
      'input[name="properties[Telefon destinatar]"]'
    );

    if (phone && phone.value.trim()) {
      const digits = phone.value.replace(/\D/g, '');

      if (digits.length < 10) {
        markField(phone, true);
        valid = false;
        if (!firstInvalid) firstInvalid = phone;
      }
    }

    const date = stepOne.querySelector('[data-patchouli-delivery-date]');

    if (date && date.value && date.value < localDateString()) {
      markField(date, true);
      valid = false;
      if (!firstInvalid) firstInvalid = date;
    }

    const error = modal.querySelector('[data-patchouli-modal-error]');

    if (error) {
      error.hidden = valid;
    }

    if (firstInvalid) {
      firstInvalid.focus();

      firstInvalid.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    return valid;
  }

  function getSelectedDrinks(modal) {
    if (!modal) return [];

    return Array.from(
      modal.querySelectorAll(`${DRINK_SELECTOR}:checked`)
    )
      .map((input) => {
        const id = Number(input.value);

        if (!Number.isFinite(id) || id <= 0) return null;

        return {
          id,
          quantity: 1,
        };
      })
      .filter(Boolean);
  }

  function updateDrinkSelection(modal) {
    if (!modal) return;

    const selected = modal.querySelectorAll(`${DRINK_SELECTOR}:checked`);
    const count = selected.length;

    const selection = modal.querySelector(
      '[data-patchouli-wine-selection]'
    );

    const countElement = modal.querySelector(
      '[data-patchouli-wine-count]'
    );

    if (countElement) {
      countElement.textContent = String(count);
    }

    if (selection) {
      selection.hidden = count === 0;
    }

    modal.querySelectorAll('[data-patchouli-wine-card]').forEach((card) => {
      const input = card.querySelector(DRINK_SELECTOR);

      card.classList.toggle(
        'is-selected',
        Boolean(input?.checked)
      );
    });
  }

  async function addDrinksToCart(drinks) {
    if (!drinks.length) return true;

    const response = await fetch(
      window.routes?.cart_add_url || '/cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: drinks,
        }),
      }
    );

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(
        data?.description ||
          data?.message ||
          'Nu am putut adăuga băuturile selectate în coș.'
      );
    }

    return true;
  }

  function setConfirmLoading(modal, loading) {
    const button = modal?.querySelector(
      '[data-patchouli-modal-confirm]'
    );

    const normalText = modal?.querySelector(
      '[data-patchouli-confirm-text]'
    );

    const loadingText = modal?.querySelector(
      '[data-patchouli-confirm-loading]'
    );

    if (button) {
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    if (normalText) normalText.hidden = loading;
    if (loadingText) loadingText.hidden = !loading;
  }

  function resetModal(modal) {
    if (!modal) return;

    modal.querySelectorAll('input, select, textarea').forEach((field) => {
      if (field.matches(DRINK_SELECTOR)) {
        field.checked = false;
        return;
      }

      if (field.type === 'hidden' || field.name === 'id') return;

      field.value = '';
      field.classList.remove('is-invalid');
      field.removeAttribute('aria-invalid');
    });

    const counter = modal.querySelector('[data-patchouli-message-count]');
    if (counter) counter.textContent = '0';

    hideValidationError(modal);
    hideCartError(modal);
    updateDrinkSelection(modal);
    showStep(modal, 1);
    prepareModal(modal);
  }

  async function approveAndSubmit(modal) {
    if (!modal) return;

    /*
     * Validăm din nou pasul 1 înainte de submit,
     * chiar dacă utilizatorul a ajuns deja în pasul 2.
     */
    if (!validateStepOne(modal)) {
      showStep(modal, 1);
      return;
    }

    hideCartError(modal);

    const formId = modal.dataset.patchouliFormId;

    let form = formId
      ? document.getElementById(formId)
      : null;

    if (!form) {
      const originalParent = modal.__patchouliOriginalParent;

      form =
        originalParent instanceof HTMLElement
          ? originalParent.closest(
              'form[data-type="add-to-cart-form"]'
            )
          : null;
    }

    if (!(form instanceof HTMLFormElement)) {
      showCartError(
        modal,
        'Nu am putut identifica produsul. Reîncarcă pagina și încearcă din nou.'
      );
      return;
    }

    const drinks = getSelectedDrinks(modal);

    setConfirmLoading(modal, true);

    try {
      /*
       * 1. Adăugăm produsele extra.
       * 2. Dawn adaugă apoi buchetul și re-randează cart drawer-ul
       *    după ce produsele extra sunt deja în coș.
       */
      if (drinks.length > 0) {
        await addDrinksToCart(drinks);
      }

      closeModal(modal, false);

      form.dataset[APPROVED_FLAG] = 'true';

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.querySelector('[type="submit"]')?.click();
      }

      window.setTimeout(() => {
        resetModal(modal);
        setConfirmLoading(modal, false);
      }, 100);
    } catch (error) {
      console.error('FreshFlowers add-to-cart error:', error);

      setConfirmLoading(modal, false);

      showCartError(
        modal,
        error?.message ||
          'A apărut o problemă la adăugarea produselor. Te rugăm să încerci din nou.'
      );
    }
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement) ||
        form.dataset.type !== 'add-to-cart-form'
      ) {
        return;
      }

      if (form.dataset[APPROVED_FLAG] === 'true') {
        delete form.dataset[APPROVED_FLAG];
        return;
      }

      const modal = getFormModal(form);
      if (!modal) return;

      if (form.id) {
        modal.dataset.patchouliFormId = form.id;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      openModal(modal);
    },
    true
  );

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const close = event.target.closest('[data-patchouli-modal-close]');

    if (close) {
      const modal = close.closest(MODAL_SELECTOR);

      if (modal?.classList.contains(OPEN_CLASS)) {
        closeModal(modal);
      }

      return;
    }

    const next = event.target.closest('[data-patchouli-next-step]');

    if (next) {
      const modal = next.closest(MODAL_SELECTOR);

      if (!validateStepOne(modal)) return;

      showStep(modal, 2);
      return;
    }

    const previous = event.target.closest('[data-patchouli-prev-step]');

    if (previous) {
      const modal = previous.closest(MODAL_SELECTOR);

      showStep(modal, 1);
      return;
    }

    const confirm = event.target.closest('[data-patchouli-modal-confirm]');

    if (confirm) {
      approveAndSubmit(confirm.closest(MODAL_SELECTOR));
    }
  });

  document.addEventListener('input', (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLTextAreaElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }

    const modal = target.closest(MODAL_SELECTOR);
    if (!modal) return;

    target.classList.remove('is-invalid');
    target.removeAttribute('aria-invalid');

    hideValidationError(modal);
    hideCartError(modal);

    if (target.matches('[data-patchouli-message]')) {
      const counter = modal.querySelector(
        '[data-patchouli-message-count]'
      );

      if (counter) {
        counter.textContent = String(target.value.length);
      }
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const modal = target.closest(MODAL_SELECTOR);
    if (!modal) return;

    target.classList.remove('is-invalid');
    target.removeAttribute('aria-invalid');

    hideValidationError(modal);
    hideCartError(modal);

    if (target.matches(DRINK_SELECTOR)) {
      updateDrinkSelection(modal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable(activeModal);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function initializeModals(root = document) {
    root
      .querySelectorAll?.(MODAL_SELECTOR)
      .forEach((modal) => {
        prepareModal(modal);

        const form = modal.closest(
          'form[data-type="add-to-cart-form"]'
        );

        if (form instanceof HTMLFormElement && form.id) {
          modal.dataset.patchouliFormId = form.id;
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeModals();
    });
  } else {
    initializeModals();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeModals(event.target);
  });

  document.addEventListener('product-info:loaded', (event) => {
    initializeModals(event.target || document);
  });
})();
